import { type RefObject, useEffect, useRef, useState } from 'react';

type Observation = { element: HTMLElement; observer: ResizeObserver };

export function useElementSize<TElement extends HTMLElement>(): {
  ref: RefObject<TElement | null>;
  width: number;
  height: number;
} {
  const ref = useRef<TElement | null>(null);
  const observationRef = useRef<Observation | null>(null);
  const [size, setSize] = useState({ width: 0, height: 0 });

  // The target can mount after the hook's owner (e.g. a layout switch), so re-check it every render.
  useEffect(() => {
    const element = ref.current;
    if (observationRef.current?.element === element) return;
    observationRef.current?.observer.disconnect();
    observationRef.current = null;
    if (!element) return;
    const observer = new ResizeObserver(([entry]) => {
      if (!entry) return;
      const { width, height } = entry.contentRect;
      setSize((previous) =>
        previous.width === width && previous.height === height ? previous : { width, height },
      );
    });
    observer.observe(element);
    observationRef.current = { element, observer };
  });

  useEffect(() => {
    const observation = observationRef;
    return () => {
      observation.current?.observer.disconnect();
      observation.current = null;
    };
  }, []);

  return { ref, width: size.width, height: size.height };
}
